import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import {
  ACCOUNT_TYPE_LABELS,
  type CheckedOperation,
  checkOperation,
  dirhams,
  type OperationType,
  type OperationValues,
  PhotoField,
  PhotoView,
  parseOperationAmount,
  present,
  type ReceiptRule,
  receiptRequired,
  receiptRequiredMessage,
} from '@horof/shared';
import { Button, Checkbox, ConfigProvider, Input, Select } from 'antd';
import { useState } from 'react';
import { useFinanceLists } from './finance-data';

/** Few projects: big buttons; more: a list. */
const PROJECT_BUTTONS = 4;

/**
 * The treasurer's form, in his order: income or expense, how much, from which
 * account, for which project, what for, when, the receipt. Category and
 * counterparty are filled on the desk (doc 38); the ones already set are kept.
 */
export function OperationForm({
  initial,
  saving,
  submitLabel,
  onSubmit,
  readOnly = false,
  savedOperation = null,
  currentReceipt = null,
  unsureChoice = false,
}: {
  initial: OperationValues;
  saving: boolean;
  submitLabel: string;
  onSubmit: (operation: CheckedOperation) => void;
  readOnly?: boolean;
  /** When correcting: the saved type, amount and source (receipt rule). */
  savedOperation?: ReceiptRule['saved'];
  currentReceipt?: { url: string; contentType: string } | null;
  /** New operations: «لست متأكداً» sends it to the desk's review. */
  unsureChoice?: boolean;
}) {
  const [{ data }] = useFinanceLists();
  const [values, setValues] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (patch: Partial<OperationValues>) =>
    setValues((v) => ({ ...v, ...patch }));
  const rule: ReceiptRule = {
    minAmount: data?.currentSettings?.receiptPhotoMinAmount ?? null,
    saved: savedOperation,
  };
  const { problems, operation } = checkOperation(values, rule);
  const photoRequired = receiptRequired(values, rule);
  const amount = parseOperationAmount(values.amount);
  const accounts = present(data?.financialAccounts?.nodes);
  const projects = present(data?.projects?.nodes).filter(
    (p) => p.status === 'OPEN' || p.id === initial.projectId,
  );

  const choose = (type: OperationType) => set({ type });

  return (
    <ConfigProvider componentDisabled={readOnly}>
      <form
        className="card operation-form"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(true);
          if (operation) onSubmit(operation);
        }}
      >
        <div className="type-choice">
          <Button
            size="large"
            className={`type-button is-credit${values.type === 'credit' ? ' is-selected' : ''}`}
            icon={<PlusCircleOutlined />}
            onClick={() => choose('credit')}
          >
            مدخول
          </Button>
          <Button
            size="large"
            className={`type-button is-debit${values.type === 'debit' ? ' is-selected' : ''}`}
            icon={<MinusCircleOutlined />}
            onClick={() => choose('debit')}
          >
            مصروف
          </Button>
        </div>

        <label className="field-label" htmlFor="amount">
          المبلغ (درهم)
        </label>
        <Input
          id="amount"
          size="large"
          inputMode="decimal"
          className="amount-input ltr-input"
          value={values.amount}
          onChange={(e) => set({ amount: e.target.value })}
        />
        {amount && <div className="field-hint">{dirhams(amount)}</div>}

        <div className="field-label">الحساب</div>
        <div className="choice-buttons">
          {accounts.map((a) => (
            <Button
              key={a.id}
              size="large"
              type={values.accountId === a.id ? 'primary' : 'default'}
              onClick={() => set({ accountId: a.id })}
            >
              {a.name}
              <span className="choice-hint">{ACCOUNT_TYPE_LABELS[a.type]}</span>
            </Button>
          ))}
        </div>

        <div className="field-label">المشروع</div>
        {projects.length <= PROJECT_BUTTONS ? (
          <div className="choice-buttons is-column">
            {projects.map((p) => (
              <Button
                key={p.id}
                size="large"
                type={values.projectId === p.id ? 'primary' : 'default'}
                onClick={() => set({ projectId: p.id })}
              >
                {p.name}
              </Button>
            ))}
          </div>
        ) : (
          <Select
            size="large"
            placeholder="اختر المشروع"
            value={values.projectId ?? undefined}
            onChange={(projectId) => set({ projectId })}
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
          />
        )}

        <label className="field-label" htmlFor="description">
          البيان: ماذا؟ ولمن؟
        </label>
        <Input.TextArea
          id="description"
          size="large"
          autoSize={{ minRows: 2, maxRows: 5 }}
          placeholder="مثلاً: فاتورة الكهرباء، أجرة العامل، مداخيل الماء…"
          value={values.description}
          onChange={(e) => set({ description: e.target.value })}
        />

        <label className="field-label" htmlFor="date">
          التاريخ
        </label>
        <Input
          id="date"
          type="date"
          size="large"
          className="ltr-input"
          value={values.date}
          onChange={(e) => set({ date: e.target.value })}
        />

        {readOnly ? (
          currentReceipt && (
            <PhotoView
              url={currentReceipt.url}
              contentType={currentReceipt.contentType}
              alt="صورة الوصل"
              caption="صورة الوصل"
            />
          )
        ) : (
          <PhotoField
            kind="RECEIPT"
            label="صورة الوصل"
            required={photoRequired}
            hint={
              photoRequired
                ? receiptRequiredMessage(rule.minAmount)
                : 'صوّر الوصل أو الفاتورة إن وُجدت.'
            }
            value={
              values.receiptPhotoId !== initial.receiptPhotoId
                ? values.receiptPhotoId
                : null
            }
            currentUrl={currentReceipt?.url ?? null}
            currentContentType={currentReceipt?.contentType ?? null}
            onChange={(receiptPhotoId) =>
              set({ receiptPhotoId: receiptPhotoId ?? initial.receiptPhotoId })
            }
            onBusyChange={setUploading}
          />
        )}

        {unsureChoice && !readOnly && (
          <Checkbox
            className="unsure"
            checked={values.needsReview}
            onChange={(e) => set({ needsReview: e.target.checked })}
          >
            لست متأكداً، تُراجع لاحقاً
          </Checkbox>
        )}

        {submitted && problems.length > 0 && (
          <ul className="field-error problems">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        {!readOnly && (
          <Button
            type="primary"
            htmlType="submit"
            block
            className="big-button"
            loading={saving}
            disabled={uploading}
          >
            {submitLabel}
          </Button>
        )}
      </form>
    </ConfigProvider>
  );
}
